import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UseNavigationBlockerProps {
  hasUnsavedChanges: boolean;
  onNavigationAttempt: () => void;
  shouldBlock?: boolean;
}

interface UseNavigationBlockerReturn {
  blockNavigation: (callback: () => void) => void;
  unblockNavigation: () => void;
}

export const useNavigationBlocker = ({
  hasUnsavedChanges,
  onNavigationAttempt,
  shouldBlock = true,
}: UseNavigationBlockerProps): UseNavigationBlockerReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const originalNavigateRef = useRef(navigate);
  const isBlockingRef = useRef(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  // Handle browser navigation (refresh, close tab, back button, etc.)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && shouldBlock) {
        // Modern browsers ignore custom messages and show a generic dialog
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome
        return ''; // Required for some browsers
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges && shouldBlock) {
        // Prevent the navigation
        event.preventDefault();
        // Push the current state back to maintain the current URL
        window.history.pushState(null, '', location.pathname + location.search);
        // Show the unsaved changes modal
        onNavigationAttempt();
      }
    };

    if (hasUnsavedChanges && shouldBlock) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);

      // Push a state to handle back button
      window.history.pushState(null, '', location.pathname + location.search);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [
    hasUnsavedChanges,
    shouldBlock,
    onNavigationAttempt,
    location.pathname,
    location.search,
  ]);

  // Override navigation for internal SPA navigation
  useEffect(() => {
    if (!shouldBlock) return;

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Override history.pushState
    window.history.pushState = function (
      state: any,
      title: string,
      url?: string | URL | null
    ) {
      if (
        hasUnsavedChanges &&
        url &&
        url !== location.pathname + location.search
      ) {
        // Store the intended navigation
        pendingNavigationRef.current = () => {
          originalPushState.call(window.history, state, title, url);
          // Trigger a popstate event to update React Router
          window.dispatchEvent(new PopStateEvent('popstate', { state }));
        };
        onNavigationAttempt();
        return;
      }
      originalPushState.call(window.history, state, title, url);
    };

    // Override history.replaceState
    window.history.replaceState = function (
      state: any,
      title: string,
      url?: string | URL | null
    ) {
      if (
        hasUnsavedChanges &&
        url &&
        url !== location.pathname + location.search
      ) {
        // Store the intended navigation
        pendingNavigationRef.current = () => {
          originalReplaceState.call(window.history, state, title, url);
          // Trigger a popstate event to update React Router
          window.dispatchEvent(new PopStateEvent('popstate', { state }));
        };
        onNavigationAttempt();
        return;
      }
      originalReplaceState.call(window.history, state, title, url);
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [
    hasUnsavedChanges,
    shouldBlock,
    onNavigationAttempt,
    location.pathname,
    location.search,
  ]);

  // Intercept clicks on navigation elements
  useEffect(() => {
    if (!shouldBlock) return;

    const handleClick = (event: MouseEvent) => {
      if (!hasUnsavedChanges) return;

      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (link && link.href) {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);

        // Check if it's an internal navigation (same origin, different path)
        if (
          url.origin === currentUrl.origin &&
          url.pathname !== currentUrl.pathname
        ) {
          event.preventDefault();
          event.stopPropagation();

          // Store the intended navigation
          pendingNavigationRef.current = () => {
            window.location.href = link.href;
          };

          onNavigationAttempt();
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges, shouldBlock, onNavigationAttempt]);

  const blockNavigation = useCallback((callback: () => void) => {
    isBlockingRef.current = true;
    pendingNavigationRef.current = callback;
  }, []);

  const unblockNavigation = useCallback(() => {
    isBlockingRef.current = false;
    if (pendingNavigationRef.current) {
      const pendingNavigation = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      // Execute the pending navigation
      setTimeout(pendingNavigation, 0);
    }
  }, []);

  return {
    blockNavigation,
    unblockNavigation,
  };
};

export default useNavigationBlocker;
